<template>
    <div class="page-default page--settings">
        <SidebarVue />
        <div class="content">
            <StatusBar :title="statusTitle" :showQuota="false" :showMenu="false" :showTpe="false" />
            <div class="content_inner">
                <div class="tpe-section" v-if="isTpeVisible">
                    <h3 class="title title--h3">Liste des Terminaux de paiement.</h3>
                    <div v-if="tpeStore.readers" class="tpe-list">
                        <div @click="readerClicked(reader)" class="card" :class="readerClasses(reader)" v-for="(reader, index) in tpeStore.readers" :key="index">
                            <h3>{{ reader.label }}</h3>
                        </div>
                    </div>
                    <div v-else>
                        Aucun TPE disponible
                    </div>
                </div>

                <!-- Section TPE Revolut -->
                <div class="tpe-revolut-section">
                    <h3 class="title title--h3">TPE de paiement Revolut</h3>
                    <div v-if="terminalStore.isLoading">
                        <span class="obloader small"></span>
                        <span>Chargement des terminaux...</span>
                    </div>
                    <div v-else-if="terminalStore.availableTerminals.length === 0">
                        <p>Aucun terminal Revolut attribué à votre organisation.</p>
                    </div>
                    <div v-else>
                        <p class="settings-hint">Sélectionnez le terminal utilisé par défaut lors des paiements :</p>
                        <div class="tpe-list">
                            <div
                                class="card"
                                :class="{ active: terminalStore.defaultTerminalId === null }"
                                @click="terminalStore.setDefault(null)"
                            >
                                <h3>Aucun (choisir à chaque paiement)</h3>
                            </div>
                            <div
                                v-for="terminal in terminalStore.availableTerminals"
                                :key="terminal._id"
                                class="card"
                                :class="{ active: terminalStore.defaultTerminalId === terminal._id }"
                                @click="terminalStore.setDefault(terminal._id)"
                            >
                                <h3>{{ terminal.label }}</h3>
                                <span class="tpe-serial">{{ terminal.serialNumber }}</span>
                            </div>
                        </div>
                        <p class="settings-hint" v-if="terminalStore.defaultTerminal">
                            Terminal par défaut : <strong>{{ terminalStore.defaultTerminal.label }}</strong>
                        </p>
                    </div>
                </div>
                <div class="print-section">
                    <h3 class="title title--h3">Impression</h3>
                    <div class="actions">
                        <Button @click="connectEpsonPrinter()"
                            v-if="printStore.getPrinterMode == PrintModeEnum.OANDB_EPSON">
                            Configurer l'imprimante EPSON
                        </Button>
                        <Button @click="connectA4Printer()"
                            v-if="printStore.getPrinterMode == PrintModeEnum.A4_AUTO">
                            Configurer l'imprimante A4
                        </Button>
                        <Button v-if="printStore.getPrinterMode === PrintModeEnum.OANDB_EPSON" variant="secondary" @click="printStore.printTestTicket()">
                            Imprimer un ticket de test
                        </Button>
                    </div>

                    <!-- Options A4 : auto-print + couleur + qualite -->
                    <div class="a4-options" v-if="printStore.getPrinterMode == PrintModeEnum.A4_AUTO">
                        <div class="a4-options__row">
                            <label class="a4-options__label">
                                <input
                                    type="checkbox"
                                    :checked="printStore.autoPrint"
                                    @change="printStore.setAutoPrint(($event.target as HTMLInputElement).checked)"
                                />
                                <span>Imprimer automatiquement les billets en fin de commande</span>
                            </label>
                            <p class="a4-options__hint">
                                Si désactivé, vous devrez cliquer sur "Imprimer" sur l'écran de récap de commande.
                            </p>
                        </div>

                        <div class="a4-options__row">
                            <label class="a4-options__label">
                                <input
                                    type="checkbox"
                                    :checked="printStore.colorPrint"
                                    @change="printStore.setColorPrint(($event.target as HTMLInputElement).checked)"
                                />
                                <span>Impression couleur</span>
                            </label>
                            <p class="a4-options__hint">
                                Active l'impression en couleur (si l'imprimante le supporte).
                            </p>
                        </div>

                        <div class="a4-options__row">
                            <label class="a4-options__label" for="print-quality">
                                <span>Qualité d'impression</span>
                            </label>
                            <select
                                id="print-quality"
                                class="a4-options__select"
                                :value="printStore.printQuality"
                                @change="printStore.setPrintQuality(Number(($event.target as HTMLSelectElement).value))"
                            >
                                <option :value="PrintQuality.DRAFT">Brouillon (150 DPI)</option>
                                <option :value="PrintQuality.NORMAL">Normale (300 DPI)</option>
                                <option :value="PrintQuality.HIGH">Haute (600 DPI)</option>
                            </select>
                            <p class="a4-options__hint">
                                Brouillon économise l'encre. Haute qualité = plus lent.
                            </p>
                        </div>
                    </div>
                    <div class="print-mode-list">
                        <div @click="printerModeClicked(printerMode)" v-for="(printerMode, index) in printerModes" :key="index" :class="printerModeClasses(printerMode)" class="card">
                            {{ printerMode.label }}
                            <template v-if="printStore.getPrinterMode == PrintModeEnum.OANDB_EPSON && printerMode.key == PrintModeEnum.OANDB_EPSON">
                                <span class="error" v-if="!printStore.connectedPrinter">Non connectée</span>
                                <template v-else>
                                    <span>Connecté à <strong>"{{ printStore.connectedPrinter }}"</strong></span>
                                </template>
                            </template>
                            <template v-if="printStore.getPrinterMode == PrintModeEnum.A4_AUTO && printerMode.key == PrintModeEnum.A4_AUTO">
                                <span class="error" v-if="!printStore.connectedPrinter">Non connectée</span>
                                <template v-else>
                                    <span>Connecté à <strong>"{{ printStore.connectedPrinter }}"</strong></span>
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <ModalBasic :modal-id="searchEpsonPrinterModalId" title="Configuration de l'imprimante">
            <div class="printer-settings-modal">
                <p>Votre imprimante EPSON <span>TM-T88V</span> ou <span>TM-T20</span> doit préalablement être installée et définit comme <span>imprimante par défaut</span>.</p>
                <div class="search" v-if="qzConnectionStatus == null && printerConnectionStatus == null">
                    <span class="obloader small"></span>
                    <span>Recherche et configuration en cours...</span>
                </div>
                <div v-if="qzConnectionStatus == false" class="connection-failed">
                    <IconSvg class="type-stroke" :name="'close'" />
                    <span class="error">
                        L'application <strong>QZ Tray</strong> doit être lancée sur votre guichet.
                        Si elle n'est pas installée vous pouvez la télécharger sur <a target="blank" href="https://qz.io/download/">ce lien</a>
                    </span>
                </div>
                <div v-else-if="printerConnectionStatus == false" class="connection-failed">
                    <IconSvg class="type-stroke" :name="'close'" />
                    <span class="error">Imprimante introuvable. Veuillez vérifier que l'imprimante soit installée (driver à jour et branchée via USB)</span>
                </div>

                <div v-if="printerConnectionStatus == false || qzConnectionStatus == false" class="actions">
                    <Button :fullWidth="false" @click="connectEpsonPrinter(false)">Relancer</Button>
                </div>
            </div>
        </ModalBasic>

        <ModalBasic :modal-id="searchA4PrintersModalId" title="Configuration de l'imprimante de bureau A4">
            <div class="printer-settings-modal">
                <div class="search" v-if="qzConnectionStatus == null && printerConnectionStatus == null">
                    <span class="obloader small"></span>
                    <span>Recherche et configuration en cours...</span>
                </div>
                <div v-if="foundPrinters">
                    <p v-for="(item, index) in foundPrinters" :key="index">
                        {{ item }}
                    </p>
                </div>
                <div v-if="qzConnectionStatus == false" class="connection-failed">
                    <IconSvg class="type-stroke" :name="'close'" />
                    <span class="error">
                        L'application <strong>QZ Tray</strong> doit être lancée sur votre guichet.
                        Si elle n'est pas installée vous pouvez la télécharger sur <a target="blank" href="https://qz.io/download/">ce lien</a>
                    </span>
                </div>
                <div v-else-if="printerConnectionStatus == false" class="connection-failed">
                    <IconSvg class="type-stroke" :name="'close'" />
                    <span class="error">Imprimante introuvable. Veuillez vérifier que l'imprimante soit installée (driver à jour et branchée via USB)</span>
                </div>

                <div v-if="printerConnectionStatus == false || qzConnectionStatus == false" class="actions">
                    <Button :fullWidth="false" @click="connectA4Printer(false)">Relancer</Button>
                </div>
            </div>
        </ModalBasic>

    </div>
</template>
<script setup lang="ts">
    import IconSvg from '@/components/atoms/ASvg.vue'
import Button from '@/components/atoms/Button.vue'
import SidebarVue from '@/components/organisms/Sidebar.vue'
import StatusBar from '@/components/organisms/StatusBar.vue'
import { useAppStore } from '@/stores/AppStore'
import { useTpeStore } from '@/stores/TpeStore'
import { PrintModeEnum, PrintQuality, usePrintStore } from '@/stores/PrintStore'
import { ref, onMounted, computed } from 'vue'
import { useEventStore } from "@/stores/EventStore"
import { useVfm } from 'vue-final-modal'
import ModalBasic from '@/components/molecules/modal/ModalBasic.vue'
import { usePaymentTerminalStore } from '@/stores/PaymentTerminalStore'

    const printStore = usePrintStore()
    const eventStore = useEventStore()
    const tpeStore = useTpeStore()
    const terminalStore = usePaymentTerminalStore()
    const appStore = useAppStore()
    const vfm = useVfm()

    const searchEpsonPrinterModalId = Symbol('searchEpsonPrinterModalId')
    const searchA4PrintersModalId = Symbol('searchA4PrintersModalId')

    type PrinterModeType = {
        key: PrintModeEnum
        label: string
        enabled: boolean
    }

    const printerModes = ref<PrinterModeType[]>([
        {
            key: PrintModeEnum.NONE,
            label: "Pas d'impression",
            enabled: true
        },
        {
            key: PrintModeEnum.OANDB_EPSON,
            label: "Imprimante thermique OandB (Epson)",
            enabled: true
        },
        {
            key: PrintModeEnum.A4,
            label: "Imprimante A4 (mode natif)",
            enabled: true
        },
        {
            key: PrintModeEnum.A4_AUTO,
            label: "Imprimante A4 (Qz Tray)",
            enabled: true
        }
    ])

    onMounted(async () => {
        if (isTpeVisible.value) {
            appStore.isLoading = true
            tpeStore.initTerminal()
            await tpeStore.discoverReaders(true)
            appStore.isLoading = false
        }
        // Charger les terminaux Revolut disponibles
        await terminalStore.fetchAvailable()
    })

    const statusTitle = computed(() => {
        if (eventStore.currentEvent) {
            return `${eventStore.currentEvent.name} - Réglages`
        }
        return ''
    })

    const printerConnectionStatus = ref(null)
    const qzConnectionStatus = ref(null)
    const foundPrinters = ref<string[]>([])

    const connectA4Printer = async (openModal: boolean = true) => {
        printerConnectionStatus.value = null
        qzConnectionStatus.value = null
        foundPrinters.value = []

        if (openModal) {
            await vfm.open(searchA4PrintersModalId)
        }

        const QZconnection = await printStore.createSocketConnection()
        if (QZconnection) {
            const printers = await printStore.findPrinters()
            qzConnectionStatus.value = true
            if (printers) {
                const printerConnection = await printStore.connectDefaultPrinter(printers[0])
                foundPrinters.value = printers
                if (printerConnection) {
                    vfm.close(searchA4PrintersModalId)
                    printerConnectionStatus.value = true
                }
            }
        }
        else {
            qzConnectionStatus.value = false
        }
    }

    const connectEpsonPrinter = async (openModal: boolean = true) => {
        printerConnectionStatus.value = null
        qzConnectionStatus.value = null

        if (openModal) {
            await vfm.open(searchEpsonPrinterModalId)
        }

        const QZconnection = await printStore.createSocketConnection()
        let printerConnection = false
        if (QZconnection) {
            qzConnectionStatus.value = true
            printerConnection = await printStore.connectDefaultPrinter()
            if (printerConnection) {
                printerConnectionStatus.value = true
                vfm.close(searchEpsonPrinterModalId)
            }
            else {
                printerConnectionStatus.value = false
            }
        }
        else {
            qzConnectionStatus.value = false
        }

    }

    const isTpeVisible = computed(() => {
        // TPE Stripe désactivé sur le guichet web (géré par l'app guichet mobile)
        return false
    })

    const printerModeClasses = (printerMode: PrinterModeType) => {
        const classes = {}
        if (printStore.getPrinterMode === printerMode.key) {
            classes['active'] = true
        }
        if (!printerMode.enabled) {
            classes['disabled'] = true
        }
        return classes
    }

    const readerClasses = (reader) => {
        const classes = {}
        if (tpeStore.reader) {
            if (tpeStore.reader.id == reader.id) {
                classes['active'] = true
            }
        }
        if (reader.status == 'offline') {
            classes['disabled'] = true
        }
        return classes
    }

    const printerModeClicked = (printerMode: PrinterModeType) => {
        if (printerMode.enabled) {
            printStore.setPrinterMode(printerMode.key)
            printStore.connectedPrinter = null
        }
    }

    const readerClicked = (async (reader) => {
        if (tpeStore.reader) {
            await tpeStore.disconnectReader(reader)
        }
        else {
            appStore.isLoading = true
            await tpeStore.connectReader(reader)
            appStore.isLoading = false
        }
    })

</script>